import React from "react";
import { Link } from "polyrouter";
import { useAcceptInviteMutation, useTenantQuery } from "generated/graphql";
import { useTranslation } from "utilities/i18n";
import { useLocation } from "react-router";
import queryString from "query-string";
import { LoadingIndicator, Animation } from "elements";
import inviteRocketLight from "assets/animations/Invite-Accept-Screen-Light.json";
import inviteRocketDark from "assets/animations/Invite-Accept-Screen-Dark.json";
import "./AcceptInvite.scss";

export const AcceptInvite: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [inviteResult, acceptInvite] = useAcceptInviteMutation();
  const loadingAcceptInvite = (
    <LoadingIndicator
      title={t("ACCEPTING_INVITATION")}
      className="theme-independent"
    />
  );

  if (inviteResult.fetching) {
    return loadingAcceptInvite;
  }

  if (location.hash && !inviteResult.data && !inviteResult.error) {
    const { code } = queryString.parse(location.hash);
    if (code && code !== "") {
      // Currently cannot pass context for notifyExchange, as notifications not displayed in this view
      acceptInvite({ code: code as string }, { notify: { handler: () => {} } });
      return loadingAcceptInvite;
    }
  }

  if (inviteResult.error) {
    return (
      <div className="accept-invite-container">
        <div className="accept-invite-content">
          <h5>{t("INVITE_ERROR")}</h5>
        </div>
      </div>
    );
  }

  if (inviteResult.data?.acceptUserInvite.invite) {
    const tenantId = inviteResult.data.acceptUserInvite.invite.tenant_id || "";
    return <InviteSuccessMessage tenantId={tenantId} />;
  } else {
    return (
      <div className="accept-invite-container">
        <div className="accept-invite-content">
          <h5 className="mt-4 mb-2">{t("INVALID_INVITATION")}</h5>
          <p>{t("INVALID_INVITATION_EXPLANATION")}</p>
        </div>
      </div>
    );
  }
};

const InviteSuccessMessage: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const isInternalInvite = tenantId === "*";
  const { t } = useTranslation();
  const { data: tenantData } = useTenantQuery({
    variables: {
      id: tenantId
    },
    pause: isInternalInvite
  });

  const tenantName = !isInternalInvite
    ? tenantData?.tenant.name
    : "All Tenants";
  return (
    <div className="accept-invite-container">
      <div className="accept-invite-content success">
        <Animation
          animation={{
            light: inviteRocketLight,
            dark: inviteRocketDark
          }}
        />
        <h1 className="mt-2">{t("INVITE_WELCOME")}</h1>
        {tenantName && <h3 className="mt-5">{tenantName}</h3>}
        <div className="mt-2">
          <Link
            to={{
              pathname: "/home",
              search: !isInternalInvite
                ? queryString.stringify({
                    tenantId: tenantId
                  })
                : undefined,
              state: { refresh: true }
            }}
            className="btn btn-primary"
          >
            {t("INVITE_GET_STARTED")}
          </Link>
        </div>
      </div>
    </div>
  );
};
